import axiosClient from '@/api/axiosClient';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useGlobalStore } from "@/store/storeGlobal.js";
import { useThemeStore } from "@/stores/themeStore";
import { useAppShellStore } from "@/stores/appShellStore";
import { useWizardModeToggleStore } from "@/store/wizardModeToggleStore";

const debugPrefix = '[UserPreferences]';

export const useUserPreferencesStore = defineStore('userPreferencesStore', () =>
{

  const userPreferences = ref();
  const globalStore = useGlobalStore();
  const themeStore = useThemeStore();
  const appShellStore = useAppShellStore();
  const wizardModeToggleStore = useWizardModeToggleStore();

  /**
   * Hydrer feature-stores (theme/appShell/wizardModeToggle) fra backend-
   * payloaden umiddelbart etter en vellykket GET /user-preferences. Hver store
   * beskytter seg selv mot loop via intern `isHydrating`-flagg, så denne
   * funksjonen er trygg å kalle gjentatte ganger (f.eks. ved cache-refresh).
   */
  function hydrateFeatureStoresFromBackend (prefs)
  {
    if (!prefs) return;
    themeStore.hydrateFromBackend(prefs);
    appShellStore.hydrateFromBackend(prefs);
    wizardModeToggleStore.hydrateFromBackend(prefs);
  }


  // try to fix timing issues with multiple api requests beeing sent upon initial page rendring
  // consider implementing something like this request manager: https://github.com/jaseeey/request-manager

  // https://jasey.blog/preventing-duplicate-api-requests-across-multiple-vue-components

  let getUserPreferencesRequest = null;
  let cachedPreferences = null;
  let cacheTimestamp = null;
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutter

  const getUserPreferences = async (forceRefresh = false) =>
  {
    // Bruk cache hvis gyldig og ikke force refresh
    if (!forceRefresh && cachedPreferences && cacheTimestamp &&
      (Date.now() - cacheTimestamp < CACHE_TTL_MS)) {
      userPreferences.value = cachedPreferences;
      globalStore.debug(`${debugPrefix} Using cached user preferences`);
      return cachedPreferences;
    }

    // Hvis det allerede er en pågående request, vent på den
    if (getUserPreferencesRequest) {
      return getUserPreferencesRequest;
    }

    getUserPreferencesRequest = (async () =>
    {
      try {
        const response = await axiosClient.get("user-preferences")
        if (response) {
          userPreferences.value = response.data
          setDefaultNumberFormat();
          cachedPreferences = userPreferences.value;
          cacheTimestamp = Date.now();
          globalStore.debug(`${debugPrefix} Fetched user preferences`, userPreferences.value);
          hydrateFeatureStoresFromBackend(userPreferences.value);
        }
      } catch (error) {
        const status = error?.response?.status;
        const problem = error?.response?.data;

        if (status === 400) {
          const detail = problem?.detail || 'Prosjekt-ID mangler i request. Send Ics-Project-Id header.';
          globalStore.debug(`${debugPrefix} Bad Request:`, detail);
          // Ikke vis notification her - dette håndteres av global interceptor eller komponenten
        } else if (status === 401) {
          globalStore.debug(`${debugPrefix} Unauthorized - må logge inn på nytt`);
          // Ikke vis notification her - dette håndteres av global interceptor
        } else {
          globalStore.debug(`${debugPrefix} Error fetching preferences:`, error);
        }
        throw error; // Re-throw for å la global interceptor eller komponent håndtere det
      } finally {
        getUserPreferencesRequest = null;
      }
    })();
    return getUserPreferencesRequest;
  };

  function setDefaultNumberFormat ()
  {
    if (!userPreferences.value.numberFormatThousandsSeparator)
      userPreferences.value.numberFormatThousandsSeparator = ' ';
    if (!userPreferences.value.numberFormatDecimalSeparator)
      userPreferences.value.numberFormatDecimalSeparator = ',';
  }

  async function saveFavorites (favorites)
  {
    await saveUserPreferences({
      dashboardComponents: userPreferences.value.dashboardComponents || [],
      homePageType: userPreferences.value.homePageType || 'dashboard',
      favorites: favorites,
      numberFormatThousandsSeparator: userPreferences.value.numberFormatThousandsSeparator || ' ',
      numberFormatDecimalSeparator: userPreferences.value.numberFormatDecimalSeparator || ',',
      dateTimeFormat: userPreferences.value.dateTimeFormat || 'DMY'
    });
  }

  async function saveUserPreferences (preferences)
  {
    // Backend expects UserPreferences object directly, not wrapped with userId
    globalStore.debug(`${debugPrefix} saveUserPreferences params: `, preferences);

    try {
      const response = await axiosClient.post("user-preferences", preferences)
      if (response) {
        globalStore.debug(`${debugPrefix} saved user preferences`)
        // Replace entire object with what was sent (backend returns empty response)
        userPreferences.value = preferences;
        cachedPreferences = preferences;
        cacheTimestamp = Date.now();
      }
    } catch (error) {
      const status = error?.response?.status;
      const problem = error?.response?.data;

      // Re-throw med forbedret error for å la komponenten håndtere det
      // Komponenten kan vise feilmelding i inputform
      if (status === 400) {
        const detail = problem?.detail || 'Ugyldig forespørsel. Prosjekt-ID mangler eller er ugyldig.';
        error.userMessage = detail;
      } else if (status === 401) {
        error.userMessage = 'Du må logge inn på nytt for å lagre innstillinger.';
      } else if (problem?.detail) {
        error.userMessage = problem.detail;
      } else if (problem?.title) {
        error.userMessage = problem.title;
      }

      throw error;
    }
  }


  return { userPreferences, getUserPreferences, saveUserPreferences, saveFavorites }
})
